import { handleResponseWithNon200Errors, makeGet, makePost } from './helpers';

/**
 * @category Shipping API
 * @see fetchFinalShippingQuote
 */
type FetchShippingQuoteType = {
    address: string;
    authToken: string;
    bidderId: number;
    catalogId: number;
    city: string;
    contact: {
        name: string;
        phoneNumber: {
            number: string;
        };
        primaryEmail: string;
    };
    country: string;
    deployment: string;
    invoiceId: string;
    itemIds: number[];
    postalCode: string;
    state: string;
};
/**
 * fetches the final invoice shipping quote via Post to Shipping API
 * @function fetchFinalShippingQuote
 * @category Shipping API
 * @param {FetchShippingQuoteType}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/shipping/quote/invoice/${invoiceId}/purchase
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2sprwrrwd6/resources/k9qsx9/methods/POST
 * @see https://github.com/LIVEauctioneers/shipping-api/tree/master/functions/PurchaseQuote-shipping-api
 */
const fetchFinalShippingQuote = ({ authToken, deployment, invoiceId, quoteId, quoteNumber }: FinalShippingQuoteType) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: `<SHIPPING-API>quote/invoice/${invoiceId}/purchase`,
        });
        request.send({ quoteId, quoteNumber });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Shipping API
 * @see fetchPurchasedShippingQuote
 */
type FinalShippingQuoteType = {
    authToken: string;
    deployment: string;
    invoiceId: string;
    quoteId: string;
    quoteNumber: string;
};
/**
 * fetches the purchase shipping quote via Post to Shipping API
 * @function fetchPurchasedShippingQuote
 * @category Shipping API
 * @param {FinalShippingQuoteType}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/shipping/quote/invoice/${invoiceId}
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2sprwrrwd6/resources/a8l892/methods/GET
 * @see https://github.com/LIVEauctioneers/shipping-api/tree/master/functions/GetInvoiceShippingInfo-shipping-api
 */
const fetchPurchasedShippingQuote = ({ authToken, deployment, invoiceId }: PurchasedShippingQuoteType) =>
    new Promise<any>((resolve, reject) => {
        const request = makeGet({
            authToken,
            deployment,
            path: `<SHIPPING-API>quote/invoice/${invoiceId}`,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

/**
 * @category Shipping API
 * @see fetchShippingQuote
 */
type PurchasedShippingQuoteType = {
    authToken: string;
    deployment: string;
    invoiceId: string;
};
/**
 * fetches a shipping quote via Post to Shipping API
 * @function fetchShippingQuote
 * @category Shipping API
 * @param {PurchasedShippingQuoteType}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/shipping/quote
 * @see https://console.aws.amazon.com/apigateway/home?region=us-east-1#/apis/2sprwrrwd6/resources/2z71rl/methods/POST
 * @see https://github.com/LIVEauctioneers/shipping-api/tree/master/functions/GetQuote-shipping-api
 */
const fetchShippingQuote = ({
    address,
    authToken,
    bidderId,
    catalogId,
    city,
    contact,
    country,
    deployment,
    invoiceId,
    itemIds,
    postalCode,
    state,
}: FetchShippingQuoteType) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({
            authToken,
            deployment,
            path: '<SHIPPING-API>quote',
        });
        request.send({
            address,
            bidderId,
            catalogId,
            city,
            contact,
            country,
            invoiceId,
            itemIds,
            postalCode,
            state,
        });
        request.end((err, response) => handleResponseWithNon200Errors({ err, reject, resolve, response }));
    });

export default {
    fetchFinalShippingQuote,
    fetchPurchasedShippingQuote,
    fetchShippingQuote,
};
